import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Alert, Button } from '@/components/ui'
import { copyToClipboard } from '@/lib/clipboard'

interface WebChatCredentialsProps {
  readonly publishableKey: string
  readonly signingSecret: string
  readonly baseUrl?: string
  readonly onDismiss: () => void
}

/**
 * Credenciais recém-geradas de um widget: a publishable key (pública) e o signing secret (mostrado UMA vez —
 * como uma API key, o Codout nunca mais o revela). Inclui o snippet de embed pronto para copiar. O signing secret
 * fica só no backend do cliente, que assina os tokens de identidade dos usuários logados.
 */
export function WebChatCredentials({
  publishableKey,
  signingSecret,
  baseUrl,
  onDismiss,
}: WebChatCredentialsProps) {
  const { t } = useTranslation()
  const apiBaseUrl = baseUrl ?? window.location.origin

  // O widget e servido pela propria aplicacao (docker/Web/Dockerfile o copia para /srv).
  // Antes isto apontava para um `cdn.codout.com` que nunca existiu: o dominio nao resolvia,
  // e o cliente colava no site dele um <script> que jamais carregaria.
  const snippet = [
    '<script',
    `  src="${apiBaseUrl}/codout-webchat.js"`,
    `  data-codout-key="${publishableKey}"`,
    `  data-codout-token="${t('webchat.credentials.snippetTokenPlaceholder')}"`,
    `  data-codout-base-url="${apiBaseUrl}"`,
    '  defer',
    '></script>',
  ].join('\n')

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-border-default bg-background-subtle p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-foreground-primary">
            {t('webchat.credentials.title')}
          </h3>
          <p className="text-sm text-foreground-muted">{t('webchat.credentials.subtitle')}</p>
        </div>
        <Button variant="ghost" size="sm" onClick={onDismiss}>
          {t('webchat.credentials.done')}
        </Button>
      </div>

      <Alert variant="warning" title={t('webchat.credentials.secretWarningTitle')}>
        {t('webchat.credentials.secretWarning')}
      </Alert>

      <CopyField label={t('webchat.credentials.publishableKey')} value={publishableKey} />
      <CopyField label={t('webchat.credentials.signingSecret')} value={signingSecret} />
      <CopyField label={t('webchat.credentials.snippet')} value={snippet} multiline />

      <p className="text-xs text-foreground-muted">{t('webchat.credentials.tokenNote')}</p>
    </div>
  )
}

interface CopyFieldProps {
  readonly label: string
  readonly value: string
  readonly multiline?: boolean
}

type CopyState = 'idle' | 'copied' | 'failed'

function CopyField({ label, value, multiline = false }: CopyFieldProps) {
  const { t } = useTranslation()
  const [state, setState] = useState<CopyState>('idle')

  const copy = () => {
    void copyToClipboard(value).then((ok) => {
      setState(ok ? 'copied' : 'failed')
      window.setTimeout(
        () => {
          setState('idle')
        },
        ok ? 1500 : 2500,
      )
    })
  }

  const buttonLabel =
    state === 'copied'
      ? t('webchat.credentials.copied')
      : state === 'failed'
        ? t('webchat.credentials.copyFailed')
        : t('webchat.credentials.copy')

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-foreground-secondary">{label}</span>
        <button
          type="button"
          onClick={copy}
          className={`text-xs font-medium hover:underline ${
            state === 'failed' ? 'text-status-danger' : 'text-action-primary'
          }`}
        >
          {buttonLabel}
        </button>
      </div>
      <code
        className={`block overflow-x-auto rounded-md border border-border-default bg-background-default px-3 py-2 font-mono text-xs text-foreground-primary ${
          multiline ? 'whitespace-pre' : 'truncate'
        }`}
      >
        {value}
      </code>
    </div>
  )
}
